/* Route-path constants for shell-owned surfaces — the single source for
   literals that more than one module needs (navigation and the settings/admin
   section registries). Module routes are NOT here: they come from each
   module's manifest (routeBase). */

export const SETTINGS_ROUTE = "/settings";

/** Full-page notification feed (PB-182). The shell owns it as a singleton tab,
   like Settings and Admin console; unmatched paths still fall through to the
   `path="*"` not-found route (PB-34) rather than redirecting home. */
export const NOTIFICATIONS_ROUTE = "/notifications";

/** Shared Teams (ADR-0082) — a workspace destination for EVERY active member,
   deliberately outside `/admin` because anybody creates a team and nothing
   there is an administrator's business. Named because three surfaces navigate
   to it: the profile menu, the app launcher's utility row, and the team pages'
   own Back control. It shipped with routes and no navigation at all, which is
   what this constant exists to stop happening again. */
export const TEAMS_ROUTE = "/teams";

/** The Admin console (PB-42): tenant administration at a top-level route,
   gated once at the parent by RequireTenantAdmin. `/settings` stays the
   personal-preferences area open to every authenticated user. */
export const ADMIN_ROUTE = "/admin";

/** Members & roles is the Admin console's first section — "where do I manage
   people?" is the first thing an admin looks for (PB-51). Named because it is
   a shared link target: the `/admin` index and the retired
   `/settings/workspace` + `/settings/users` URLs all redirect here. */
export const ADMIN_MEMBERS_ROUTE = `${ADMIN_ROUTE}/members`;

/** Directory group -> tenant role rules (PB-136 — PPL-04). Named because the
   Identity & directory page links to it, and a link target two surfaces share
   must not be a duplicated literal. */
export const ADMIN_DIRECTORY_GROUP_ROLES_ROUTE = `${ADMIN_ROUTE}/directory-group-roles`;

/** Manual directory-group membership — TEMPORARY, ahead of PB-63 (Platform
   People & Organization Directory sync/UI). Named for the same reason as its
   sibling above: the Identity & directory page links to it. Delete this
   constant along with the page/route/link once PB-63 lands. */
export const ADMIN_DIRECTORY_GROUP_MEMBERSHIP_ROUTE = `${ADMIN_ROUTE}/directory-group-membership`;

/** Integrations (PB-4624). Named for the same reason `ADMIN_MEMBERS_ROUTE` is:
   the retired `/settings/integrations` URL redirects here, so it is a link
   target two surfaces share and must not be a duplicated literal — nor reached
   through a non-null assertion on the map below, whose index signature cannot
   know the key exists. */
export const ADMIN_INTEGRATIONS_ROUTE = `${ADMIN_ROUTE}/integrations`;

/** Adding an integration is a PAGE, not a panel inside the list.
 *
 * A dedicated URL is what makes the flow survive the things that happen during
 * it: the OAuth round trip leaves the tab, a half-filled form should be
 * reachable by back button, and the step someone is on should be linkable to a
 * colleague. Component state could carry none of those.
 *
 * A static segment, so React Router's own ranking puts it ahead of the sibling
 * `integrations/:integrationId` — `new` must never be read as an id. */
export const ADMIN_INTEGRATIONS_NEW_ROUTE = `${ADMIN_INTEGRATIONS_ROUTE}/new`;

/** One integration's detail page. Encoded because the id reaches the URL as a
 * path SEGMENT, the same reason {@link settingsKpiDetailRoute} encodes its own —
 * and named because the inventory rows, the add flow and the editor's own Back
 * all build it. */
export function adminIntegrationRoute(integrationId: string): string {
  return `${ADMIN_INTEGRATIONS_ROUTE}/${encodeURIComponent(integrationId)}`;
}

/** A section within one integration (`connections`, `tool-access`, `people`).
 * Built from {@link adminIntegrationRoute} so the id is encoded exactly once. */
export function adminIntegrationSectionRoute(
  integrationId: string,
  section: string,
): string {
  return `${adminIntegrationRoute(integrationId)}/${section}`;
}

/** Admin section id → absolute route. One entry per rail item. */
export const ADMIN_SECTION_ROUTES: Record<string, string> = {
  members: ADMIN_MEMBERS_ROUTE,
  organization: `${ADMIN_ROUTE}/organization`,
  identity: `${ADMIN_ROUTE}/identity`,
  modules: `${ADMIN_ROUTE}/modules`,
  communication: `${ADMIN_ROUTE}/communication`,
  audit: `${ADMIN_ROUTE}/audit`,
  genai: `${ADMIN_ROUTE}/genai`,
  integrations: ADMIN_INTEGRATIONS_ROUTE,
  health: `${ADMIN_ROUTE}/health`,
};

/** Directory group -> tenant role rules (PB-136 — PPL-04). Named here because
   the Users & Access entry point links to it, and a link target two surfaces

/** KPI Manager (PB-94) and its dedicated Create KPI page. Named because three
   surfaces link at them: the settings overview tile, the KPI host's own
   create/cancel navigation, and the breadcrumb's parent crumb. */
export const SETTINGS_KPI_ROUTE = `${SETTINGS_ROUTE}/kpi`;
/** KPI Manager child views in the shared Settings rail (PB-5134, d3). Static
   segments like `create`: they can never be read as a KPI id. */
export const SETTINGS_KPI_LIBRARY_ROUTE = `${SETTINGS_KPI_ROUTE}/library`;
export const SETTINGS_KPI_ARCHIVED_ROUTE = `${SETTINGS_KPI_ROUTE}/archived`;
export const SETTINGS_KPI_CREATE_ROUTE = `${SETTINGS_KPI_ROUTE}/create`;

/** A KPI's detail page (PB-2941). The id is encoded because it reaches the URL
 * as a path SEGMENT — a value carrying `/`, `?` or `#` would otherwise reshape
 * the route, the same reason the KPI API client encodes it. */
export function settingsKpiDetailRoute(kpiId: string): string {
  return `${SETTINGS_KPI_ROUTE}/${encodeURIComponent(kpiId)}`;
}

/** A KPI's edit page (PB-2941) — a child of its detail page, so Back from the
 * form lands on the KPI rather than on the collection. Built from
 * {@link settingsKpiDetailRoute} so the id is encoded exactly once, in one
 * place. */
export function settingsKpiEditRoute(kpiId: string): string {
  return `${settingsKpiDetailRoute(kpiId)}/edit`;
}

/** A member's own connections (PB-1592). Named for the same reason
   `ADMIN_INTEGRATIONS_ROUTE` is: the PUBLIC OAuth completion page links back
   here, so it is a link target two surfaces share and must not be a duplicated
   literal — nor reached through the index signature of the map below. */
export const SETTINGS_CONNECTIONS_ROUTE = `${SETTINGS_ROUTE}/connections`;

/** Catalogue of administrator-approved integrations a member may connect to. */
export const SETTINGS_CONNECTIONS_NEW_ROUTE = `${SETTINGS_CONNECTIONS_ROUTE}/new`;

/** Settings section id → absolute route. One entry per rail item.
   `player-ai` is the index route (`/settings`), so everyone lands on a usable
   personal-preferences page. Tenant administration moved to `/admin` (PB-42),
   and PB-4624 finished that move: `integrations` (PB-1591) now lives at
   `/admin/integrations`, gated once at the `/admin` parent like every other
   admin section, so it needs no entry here and the retired URL redirects.

   `kpi` is the one admin-ish section that deliberately STAYS: it stopped gating
   at the route in PB-2941 — a KPI's owner may be a plain member and still has
   to reach its detail page, so KpiSettings applies the gate per URL instead.
   That per-row exception is why `kpi` did not follow `integrations` to `/admin`.

   `connections` is deliberately NOT admin-gated at all: a member who cannot
   configure an integration must still be able to connect to one. */
/** Player AI: General at the section URL; Connected apps at its own URL (PB-5134, d16). */
export const SETTINGS_PLAYER_AI_ROUTE = `${SETTINGS_ROUTE}/player-ai`;
export const SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE = `${SETTINGS_PLAYER_AI_ROUTE}/connected-apps`;

export const SETTINGS_SECTION_ROUTES: Record<string, string> = {
  /* PB-5134: Account is the index route now; the tile overview is gone. */
  account: SETTINGS_ROUTE,
  "player-ai": SETTINGS_PLAYER_AI_ROUTE,
  launcher: `${SETTINGS_ROUTE}/launcher`,
  notifications: `${SETTINGS_ROUTE}/notifications`,
  kpi: SETTINGS_KPI_ROUTE,
  connections: SETTINGS_CONNECTIONS_ROUTE,
};
